import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserHttpService } from '../../user/services/user-http.service';
import { FavoriteGraph, FavoriteGraphDict, GenericFavoriteService } from '../models/favorite';
import { PinUnpinService } from '../../devices/dashboard/services/pin-unpin.service';
import { ToastService } from '../../common/services/toast/toast.service';
import { HttpParams } from '@angular/common/http';
import { v4 } from 'uuid';
import { cloneDeep } from 'lodash';

@Injectable({
    providedIn: 'root'
})
export class FavoriteGraphsService implements GenericFavoriteService<FavoriteGraphDict, FavoriteGraph> {

    constructor(private httpService: UserHttpService,
                private toastService: ToastService,
                private pinUnpinService: PinUnpinService) {
        this.initDefaultValues();
        this.getFavoritesData();
    }
    private key = 'favoriteGraphs';

    public favorites$: Observable<FavoriteGraphDict>;
    private favoritesSubject: BehaviorSubject<FavoriteGraphDict>;
    private favorites: FavoriteGraphDict;

    private initDefaultValues() {
        this.favorites = {};
        this.favoritesSubject = new BehaviorSubject<FavoriteGraphDict>(null);
        this.favorites$ = this.favoritesSubject.asObservable();
    }

    private getFavoritesData() {

        this.getUserFavoritesGraphs().toPromise().then(result => {
            if (result != null) {
                for (const dictkey in result) {
                    if (result.hasOwnProperty(dictkey)) {
                        let favorite = result[dictkey];

                        //pull properties from first element and shift
                        favorite["spaceId"] = favorite.pinnedGraphs[0].spaceId;
                        favorite["controlId"] = favorite.pinnedGraphs[0].controlId;
                        favorite["locationId"] = favorite.pinnedGraphs[0].locationId;
                        favorite["graphId"] = favorite.pinnedGraphs[0].graphId;
                        favorite.pinnedGraphs.shift();

                        this.updateSpaceIds(favorite, true);

                        this.favorites[favorite.id] = favorite;
                    }
                }

                this.notifyObserversFavoritesChanged();
            }
        }).catch(reason => {
            // ignore
        });

    }

    private notifyObserversFavoritesChanged() {
        const next = JSON.parse(JSON.stringify(this.favorites));
        this.favoritesSubject.next(next);
    }

    public toggleFavorite(favorite: FavoriteGraph) {
        if (this.isFavorite(favorite)) {
            this.removeFavorite(favorite);
        } else {
            this.addFavorite(favorite);
        }
    }

    public isFavorite(favorite: FavoriteGraph) {
        if (this.favorites[favorite.id]) {
            return this.favorites[favorite.id].graphId === favorite.graphId;
        }
        return false;
    }

    private removeFavorite(favorite: FavoriteGraph) {

        this.deleteUserFavoritesGraph(favorite.id).toPromise().then(result => {
            if (result == true) {

                delete this.favorites[favorite.id];

//                this.pinUnpinService.unpinAllFavoriteGraphs();

                this.notifyObserversFavoritesChanged();

                this.toastService.showSimple('Removed from favorites.');
            }
        });
    }

    public addFavorite(favorite: FavoriteGraph): Observable<any> {
        const res: Observable<any> = this.postUserFavoritesGraph(cloneDeep(favorite));
        res.toPromise().then(result => {
            if (result != null) {

                //pull properties from first element and shift
                result["spaceId"] = result.pinnedGraphs[0].spaceId;
                result["controlId"] = result.pinnedGraphs[0].controlId;
                result["locationId"] = result.pinnedGraphs[0].locationId;
                result["graphId"] = result.pinnedGraphs[0].graphId;
                result.pinnedGraphs.shift();

                this.updateSpaceIds(result, true);

                this.favorites[result.id] = {
                    id: result.id,
                    locationId: result.locationId,
                    spaceId: result.spaceId,
                    controlId: result.controlId,
                    graphCustomName: result.graphCustomName,
                    graphId: result.graphId,
                    pinnedGraphs: result.pinnedGraphs
                } as FavoriteGraph;
                this.notifyObserversFavoritesChanged();
                this.toastService.showSimple('Added to favorites.');
            }
        });
        return res;
    }

    public updateSpaceIds(favorite: FavoriteGraph, fromNull: boolean) {
        favorite.spaceId = fromNull ?
            (favorite.spaceId == null ? -1 : favorite.spaceId) :
            (favorite.spaceId === -1 ? null : favorite.spaceId);
        if (favorite.pinnedGraphs != null) {
            favorite.pinnedGraphs.forEach(pinnedGraph => {
                if (fromNull) {
                    pinnedGraph.spaceId = pinnedGraph.spaceId == null ? -1 : pinnedGraph.spaceId;
                } else {
                    pinnedGraph.spaceId = pinnedGraph.spaceId === -1 ? null : pinnedGraph.spaceId;
                }
            });

        }
    }

    private postUserFavoritesGraph(favorite: any): Observable<any> {
        const id: string = v4();
        favorite.id = id;

        //push to first element in array and delete properties
        if (!favorite.pinnedGraphs) {
            favorite.pinnedGraphs = [];
            favorite.pinnedGraphs.push({ spaceId: favorite.spaceId, controlId: favorite.controlId, locationId: favorite.locationId, graphId: favorite.graphId });
        } else {
            favorite.pinnedGraphs.unshift({ spaceId: favorite.spaceId, controlId: favorite.controlId, locationId: favorite.locationId, graphId: favorite.graphId });
        }
        delete favorite["spaceId"];
        delete favorite["controlId"];
        delete favorite["locationId"];
        delete favorite["graphId"];

        favorite.pinnedGraphs.forEach(item => {
            delete item["spaceId"];
            delete item["controlId"];
            delete item["locationId"];
        });

        return this.httpService.post<any>('user/favorites/graphs', favorite);
    }

    private deleteUserFavoritesGraph(id: string): Observable<boolean> {
        return this.httpService.delete<boolean>(`user/favorites/graphs/${id}`);
    }

    private getUserFavoritesGraphs(): Observable<any> {
        return this.httpService.get<FavoriteGraph[]>('user/favorites/graphs');
    }

    public getFavoritesGraphsClones(deviceId: number): Observable<any> {
        let params = new HttpParams();
        params = params.append('controlId', deviceId.toString());

        return this.httpService.get<any>('user/favorites/graphs/clones', { params: params });
    }

    public getFavoritesGraphsClone(id : string, deviceId: number): Observable<any> {
        let params = new HttpParams();
        params = params.append('controlId', deviceId.toString());

        return this.httpService.get<any>(`user/favorites/graphs/${id}/clone`, { params: params });
    }
}
